/**
 * ObjectiveAutoWizardPage
 *
 * Modo Auto end-to-end:
 *
 *   1. ANALYZING  → animación de análisis + dispara suggest() en paralelo
 *   2. BUILDING   → la IA arma los recursos:
 *                   - segmento: crea uno nuevo a partir del prompt sugerido
 *                   - plantilla: elige una aprobada existente (la mejor disponible)
 *                   - cadencia: usa la sugerencia
 *   3. REVIEWING  → muestra los 3 recursos armados, todos editables inline
 *   4. ACTIVATING → guarda en la campaña + activa + navega al dashboard
 *
 * Si algún recurso no se puede armar (ej: no hay plantillas aprobadas),
 * se muestra un fallback claro con CTA para resolverlo.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BrainCircuit,
  Check,
  Loader2,
  Sparkles,
  AlertCircle,
  Users,
  FileText,
  Clock,
  Zap,
  Pencil,
  X,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { objectivesApi } from "../api";
import { segmentsApi } from "../../segments/api";
import { whatsappApi } from "../../whatsapp/api";
import type {
  Objective,
  ObjectiveSuggestion,
  ObjectiveCadence,
} from "../types";
import type { Segment } from "../../segments/types";
import type { WhatsAppTemplate } from "../../whatsapp/types";
import { PRESET_BY_KEY } from "../types";
import { SegmentPicker } from "../components/SegmentPicker";
import { TemplatePicker } from "../components/TemplatePicker";
import { CadenceConfigurator } from "../components/CadenceConfigurator";

const MOCK_BRAND_ID = "brand-demo";

const ANALYSIS_STEPS = [
  "Analizando historial de compras",
  "Detectando patrones de comportamiento",
  "Segmentando audiencia con RFM",
  "Eligiendo plantilla óptima",
  "Calculando mejor horario de envío",
];

type Phase = "analyzing" | "building" | "reviewing" | "activating";

interface BuiltResources {
  segment: Segment | null;
  segmentCreated: boolean;
  template: WhatsAppTemplate | null;
  templateMissing: boolean;
  cadence: ObjectiveCadence;
}

export function ObjectiveAutoWizardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [objective, setObjective] = useState<Objective | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>("analyzing");
  const [analysisIdx, setAnalysisIdx] = useState(0);
  const [suggestion, setSuggestion] = useState<ObjectiveSuggestion | null>(
    null,
  );
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [built, setBuilt] = useState<BuiltResources | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [editing, setEditing] = useState<
    "segment" | "template" | "cadence" | null
  >(null);

  const buildStartedRef = useRef(false);

  /* ─── Load objective ─── */
  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const res = await objectivesApi.getById(id);
      if (res.success) setObjective(res.data);
      else setLoadError(res.error || "No se pudo cargar la campaña");
      setLoading(false);
    })();
  }, [id]);

  /* ─── Phase 1: analyze + fetch suggestion ─── */
  useEffect(() => {
    if (!objective || phase !== "analyzing") return;

    let cancelled = false;
    const tick = setInterval(() => {
      if (!cancelled) setAnalysisIdx((i) => i + 1);
    }, 600);

    (async () => {
      try {
        const res = await objectivesApi.suggest(objective.preset);
        if (cancelled) return;
        if (res.success) setSuggestion(res.data);
        else
          setSuggestError(
            res.error || "No pudimos generar la propuesta de la IA.",
          );
      } catch {
        if (!cancelled)
          setSuggestError("No pudimos generar la propuesta de la IA.");
      }
    })();

    const advance = setTimeout(() => {
      if (!cancelled) setPhase("building");
    }, ANALYSIS_STEPS.length * 600 + 400);

    return () => {
      cancelled = true;
      clearInterval(tick);
      clearTimeout(advance);
    };
  }, [objective, phase]);

  /* ─── Phase 2: build resources ─── */
  const buildResources = useCallback(async () => {
    if (!suggestion || !objective || buildStartedRef.current) return;
    buildStartedRef.current = true;

    const result: BuiltResources = {
      segment: null,
      segmentCreated: false,
      template: null,
      templateMissing: false,
      cadence: suggestion.suggestedCadence,
    };

    /* Segment: crea uno nuevo usando el endpoint mock (sin IA).
       Si falla por cualquier motivo, cae al primer segmento guardado. */
    try {
      const mockRes = await segmentsApi.createMock({
        brandId: MOCK_BRAND_ID,
        name: `${objective.name} · audiencia IA`,
        description: suggestion.suggestedSegmentPrompt,
      });
      if (mockRes.success && mockRes.data) {
        result.segment = mockRes.data;
        result.segmentCreated = true;
      }
    } catch {
      /* fallthrough */
    }

    if (!result.segment) {
      try {
        const listRes = await segmentsApi.list(MOCK_BRAND_ID, "saved");
        if (listRes.success && listRes.data.length > 0) {
          result.segment = listRes.data[0];
        }
      } catch {
        /* leave null */
      }
    }

    /* Template: pick first approved available */
    try {
      const tplRes = await whatsappApi.getTemplates(MOCK_BRAND_ID);
      if (tplRes.success) {
        const approved = tplRes.data.filter(
          (t) => t.approvalStatus === "approved",
        );
        if (approved.length > 0) result.template = approved[0];
        else result.templateMissing = true;
      } else {
        result.templateMissing = true;
      }
    } catch {
      result.templateMissing = true;
    }

    setBuilt(result);
    setPhase("reviewing");
  }, [suggestion, objective]);

  useEffect(() => {
    if (phase === "building" && suggestion) buildResources();
  }, [phase, suggestion, buildResources]);

  // Audit OBJ-01: evita el spinner infinito si suggest() nunca resuelve.
  useEffect(() => {
    if (phase !== "building" || suggestion || suggestError) return;
    const t = setTimeout(
      () =>
        setSuggestError(
          "La propuesta está tardando demasiado. Reintentá o configurá la campaña a mano.",
        ),
      10000,
    );
    return () => clearTimeout(t);
  }, [phase, suggestion, suggestError]);

  const retrySuggest = () => {
    setSuggestError(null);
    setSuggestion(null);
    setBuilt(null);
    buildStartedRef.current = false;
    setAnalysisIdx(0);
    setPhase("analyzing");
  };

  /* ─── Activate ─── */
  const handleActivate = async () => {
    if (!id || !built || !built.segment || !built.template) return;
    setActionError(null);
    setPhase("activating");

    const updateRes = await objectivesApi.update(id, {
      segmentId: built.segment.id,
      templateId: built.template.id,
      cadence: built.cadence,
    });
    if (!updateRes.success) {
      setActionError(updateRes.error || "Error al guardar configuración");
      setPhase("reviewing");
      return;
    }

    // Custom → revisión IA. Otros → activate directo (compat con auto-flow de always-on).
    if (objective?.origin === "custom") {
      const submitRes = await objectivesApi.submitForReview(id);
      if (!submitRes.success) {
        setActionError(submitRes.error || "Error al enviar a revisión");
        setPhase("reviewing");
        return;
      }
      navigate(`/objectives?review=${id}`);
      return;
    }

    const activateRes = await objectivesApi.activate(id);
    if (!activateRes.success) {
      setActionError(activateRes.error || "Error al activar la campaña");
      setPhase("reviewing");
      return;
    }

    navigate(`/objectives/${id}`);
  };

  const presetMeta = useMemo(
    () => (objective ? PRESET_BY_KEY[objective.preset] : null),
    [objective],
  );
  const canActivate = !!built?.segment && !!built?.template;

  if (loading) return <CenteredLoader text="Cargando…" />;
  if (loadError || !objective)
    return (
      <CenteredError
        message={loadError || "Campaña no encontrada"}
        backHref="/objectives"
      />
    );

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-3xl space-y-6">
        <Link
          to={`/objectives/${id}/setup`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
        >
          <ArrowLeft size={14} />
          Cambiar tipo de configuración
        </Link>

        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="text-3xl leading-none">{objective.emoji}</span>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              {objective.name}
            </h1>
            <span className="px-2 py-0.5 bg-gradient-to-r from-violet-100 to-fuchsia-100 text-violet-700 text-[10px] font-bold rounded-full uppercase tracking-wider">
              Modo IA
            </span>
          </div>
          <p className="text-slate-500">{presetMeta?.shortDescription}</p>
        </div>

        {suggestError && (phase === "analyzing" || phase === "building") ? (
          <SuggestErrorScreen
            message={suggestError}
            onRetry={retrySuggest}
            manualHref={`/objectives/${id}/setup/manual`}
          />
        ) : (
          <>
            {phase === "analyzing" && <AnalyzingScreen idx={analysisIdx} />}
            {phase === "building" && <BuildingScreen />}
          </>
        )}

        {phase === "reviewing" && built && suggestion && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border border-violet-100 p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                  <Sparkles size={18} className="text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-1">
                    Lo que armé para vos
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {suggestion.rationale}
                  </p>
                </div>
              </div>
            </div>

            <ResourceCard
              icon={Users}
              title="Audiencia"
              status={
                built.segment
                  ? built.segmentCreated
                    ? "created"
                    : "reused"
                  : "missing"
              }
              expanded={editing === "segment"}
              onEdit={() =>
                setEditing(editing === "segment" ? null : "segment")
              }
            >
              {editing !== "segment" && built.segment && (
                <div>
                  <p className="font-semibold text-slate-900 text-sm">
                    {built.segment.name || "Sin nombre"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {(built.segment.lastKnownCount || 0).toLocaleString()}{" "}
                    usuarios coinciden
                    {built.segment.conversationSummary && (
                      <>
                        {" · "}
                        <span className="italic">
                          {built.segment.conversationSummary}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
              {editing !== "segment" && !built.segment && (
                <p className="text-sm text-amber-700">
                  No hay segmentos disponibles. Configurá uno para continuar.
                </p>
              )}
              {editing === "segment" && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <SegmentPicker
                    brandId={MOCK_BRAND_ID}
                    selectedId={built.segment?.id}
                    onSelect={(s) => {
                      setBuilt({
                        ...built,
                        segment: s,
                        segmentCreated: false,
                      });
                      setEditing(null);
                    }}
                  />
                </div>
              )}
            </ResourceCard>

            <ResourceCard
              icon={FileText}
              title="Plantilla"
              status={
                built.template
                  ? "reused"
                  : built.templateMissing
                    ? "missing"
                    : "loading"
              }
              expanded={editing === "template"}
              onEdit={() =>
                setEditing(editing === "template" ? null : "template")
              }
            >
              {editing !== "template" && built.template && (
                <div>
                  <p className="font-mono font-semibold text-slate-900 text-sm truncate">
                    {built.template.name}
                  </p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2 whitespace-pre-wrap leading-relaxed">
                    {built.template.body}
                  </p>
                </div>
              )}
              {editing !== "template" &&
                !built.template &&
                built.templateMissing && (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-700">
                      No tenés plantillas aprobadas todavía.
                    </p>
                    <button
                      type="button"
                      onClick={() => setEditing("template")}
                      className="inline-flex items-center gap-1.5 text-xs text-violet-600 hover:underline font-medium"
                    >
                      Crear una acá mismo
                      <ArrowRight size={11} />
                    </button>
                  </div>
                )}
              {editing === "template" && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <TemplatePicker
                    brandId={MOCK_BRAND_ID}
                    selectedId={built.template?.id}
                    onSelect={(t) => {
                      setBuilt({
                        ...built,
                        template: t,
                        templateMissing: false,
                      });
                      setEditing(null);
                    }}
                  />
                </div>
              )}
            </ResourceCard>

            <ResourceCard
              icon={Clock}
              title="Horario y frecuencia"
              status="created"
              expanded={editing === "cadence"}
              onEdit={() =>
                setEditing(editing === "cadence" ? null : "cadence")
              }
            >
              {editing !== "cadence" && (
                <p className="text-sm text-slate-700">
                  {built.cadence.kind === "recurring"
                    ? `Todos los días entre ${built.cadence.sendWindow.start} y ${built.cadence.sendWindow.end} · ${(built.cadence.daysOfWeek || []).length} días por semana`
                    : `Una sola vez · ${built.cadence.sendWindow.start} – ${built.cadence.sendWindow.end}`}
                </p>
              )}
              {editing === "cadence" && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <CadenceConfigurator
                    cadence={built.cadence}
                    onChange={(c) => setBuilt({ ...built, cadence: c })}
                  />
                  <div className="flex justify-end mt-3">
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="text-xs text-violet-600 hover:underline font-medium"
                    >
                      Cerrar edición
                    </button>
                  </div>
                </div>
              )}
            </ResourceCard>

            {actionError && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
                <AlertCircle size={18} />
                <span className="text-sm font-medium">{actionError}</span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to={`/objectives/${id}/setup/manual`} className="flex-1">
                <Button variant="secondary" className="w-full">
                  <Pencil size={15} />
                  Cambiar al modo manual
                </Button>
              </Link>
              <Button
                variant="primary"
                onClick={handleActivate}
                disabled={!canActivate}
                className="flex-1"
              >
                <Zap size={15} />
                {objective?.origin === "custom"
                  ? "Enviar a revisión IA"
                  : "Aprobar y activar"}
                <ArrowRight size={15} />
              </Button>
            </div>

            {!canActivate && (
              <p className="text-xs text-amber-700 text-center bg-amber-50 border border-amber-100 px-3 py-2 rounded-lg">
                <AlertTriangle size={12} className="inline mr-1" />
                Falta resolver{" "}
                {!built.segment && !built.template
                  ? "audiencia y plantilla"
                  : !built.segment
                    ? "la audiencia"
                    : "la plantilla"}{" "}
                para activar.
              </p>
            )}
          </div>
        )}

        {phase === "activating" && <ActivatingScreen />}
      </div>
    </div>
  );
}

/* ─── Sub-screens ─── */

function AnalyzingScreen({ idx }: { idx: number }) {
  return (
    <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border border-violet-100 p-8 md:p-10 text-center">
      <div className="w-16 h-16 bg-white rounded-full shadow-md flex items-center justify-center mx-auto mb-5">
        <BrainCircuit size={28} className="text-violet-600 animate-pulse" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">
        La IA está pensando
      </h2>
      <p className="text-slate-600 mb-8 max-w-sm mx-auto leading-relaxed">
        Analizando tus datos para proponer el mejor segmento, plantilla y
        cadencia.
      </p>

      <div className="space-y-3 max-w-md mx-auto">
        {ANALYSIS_STEPS.map((stepLabel, i) => {
          const done = i < idx;
          const active = i === idx;
          return (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                done
                  ? "bg-white border border-emerald-100"
                  : active
                    ? "bg-white border border-violet-200 shadow-sm"
                    : "bg-white/50 border border-slate-100"
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  done
                    ? "bg-emerald-500"
                    : active
                      ? "bg-violet-600"
                      : "bg-slate-200"
                }`}
              >
                {done ? (
                  <Check size={12} className="text-white" />
                ) : active ? (
                  <Loader2 size={11} className="text-white animate-spin" />
                ) : (
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full" />
                )}
              </div>
              <span
                className={`text-sm text-left flex-1 ${
                  done
                    ? "text-slate-700"
                    : active
                      ? "text-slate-900 font-medium"
                      : "text-slate-400"
                }`}
              >
                {stepLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BuildingScreen() {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 md:p-10 text-center">
      <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Loader2 className="animate-spin text-violet-600" size={26} />
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-1">
        Armando tu campaña
      </h2>
      <p className="text-sm text-slate-500">
        Creando segmento, seleccionando plantilla, calibrando horarios…
      </p>
    </div>
  );
}

function SuggestErrorScreen({
  message,
  onRetry,
  manualHref,
}: {
  message: string;
  onRetry: () => void;
  manualHref: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-8 md:p-10 text-center animate-fade-in">
      <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="text-red-500" size={26} />
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-1">
        No pudimos armar la propuesta
      </h2>
      <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto leading-relaxed">
        {message}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="primary" onClick={onRetry}>
          <Sparkles size={15} />
          Reintentar
        </Button>
        <Link to={manualHref}>
          <Button variant="secondary">
            Configurar a mano
            <ArrowRight size={15} />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function ActivatingScreen() {
  return (
    <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-8 md:p-10 text-center">
      <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Loader2 className="animate-spin text-emerald-600" size={26} />
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-1">
        Activando tu campaña
      </h2>
      <p className="text-sm text-slate-600">
        Listo en un segundo. Vas a ir al dashboard automáticamente.
      </p>
    </div>
  );
}

function CenteredLoader({ text }: { text: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex items-center gap-3 text-slate-500">
        <Loader2 size={20} className="animate-spin" />
        <span>{text}</span>
      </div>
    </div>
  );
}

function CenteredError({
  message,
  backHref,
}: {
  message: string;
  backHref: string;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <p className="text-red-700 font-medium mb-3">{message}</p>
        <Link to={backHref} className="text-violet-600 text-sm hover:underline">
          Volver
        </Link>
      </div>
    </div>
  );
}

/* ─── ResourceCard ─── */
type ResourceStatus = "created" | "reused" | "missing" | "loading";

interface ResourceCardProps {
  icon: typeof Users;
  title: string;
  status: ResourceStatus;
  expanded: boolean;
  onEdit: () => void;
  children: React.ReactNode;
}

function ResourceCard({
  icon: Icon,
  title,
  status,
  expanded,
  onEdit,
  children,
}: ResourceCardProps) {
  const statusMeta: Record<
    ResourceStatus,
    { label: string; classes: string; icon: typeof CheckCircle2 | null }
  > = {
    created: {
      label: "Creado por IA",
      classes: "bg-violet-50 text-violet-700 border-violet-100",
      icon: Sparkles,
    },
    reused: {
      label: "Existente",
      classes: "bg-emerald-50 text-emerald-700 border-emerald-100",
      icon: CheckCircle2,
    },
    missing: {
      label: "Falta resolver",
      classes: "bg-amber-50 text-amber-700 border-amber-100",
      icon: AlertTriangle,
    },
    loading: {
      label: "Buscando…",
      classes: "bg-slate-50 text-slate-500 border-slate-100",
      icon: null,
    },
  };
  const meta = statusMeta[status];
  const StatusIcon = meta.icon;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              status === "missing"
                ? "bg-amber-100"
                : status === "loading"
                  ? "bg-slate-100"
                  : "bg-violet-100"
            }`}
          >
            <Icon
              size={16}
              className={
                status === "missing"
                  ? "text-amber-600"
                  : status === "loading"
                    ? "text-slate-400"
                    : "text-violet-600"
              }
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="font-semibold text-slate-900 text-sm">{title}</p>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${meta.classes}`}
              >
                {StatusIcon && <StatusIcon size={9} />}
                {meta.label}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-slate-500 hover:text-violet-600 font-medium flex items-center gap-1 flex-shrink-0"
          >
            {expanded ? (
              <>
                <X size={12} />
                Cerrar
              </>
            ) : (
              <>
                <Pencil size={12} />
                Cambiar
              </>
            )}
          </button>
        </div>
        <div className="pl-12">{children}</div>
      </div>
    </div>
  );
}

export default ObjectiveAutoWizardPage;
